import { ResponsiveEyeSelection, ResponsiveLogmarSelection, Flex, NavButtons } from '../../../2022-component-library';
import { useState, useEffect, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { ResponsiveContrastSensitivityTestOptician, ResponsiveContrastSensitivityResults } from '.';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { useResultData } from '../../../helpers/useResultData';

const ResponsiveContrastSensitivityContainer = ({ setActiveTab, setTestStep }) => {
  const { testData, updateTestData } = useTestContext();
  const { clearCurrentEyeResults } = useResultData();
  const [step, setStep] = useState('eyeSetup');
  const [nextStep, setNextStep] = useState('');
  const [backStep, setBackStep] = useState('');
  const [canProgress, setCanProgress] = useState(false);
  const [logmar, setLogmar] = useState(null);
  const postMessage = usePostMessage();
  const smallScreen = useMediaQuery('(max-height: 450px)');
  const receivedMessage = useReceiveMessage();

  useEffect(() => {
    let selectedEye;
    switch (true) {
      case step === 'eyeSetup':
        setBackStep('selectTest');
        setNextStep(testData.testBothEyes ? 'bothEyesSetup' : testData.testRightEye ? 'rightEyeSetup' : 'leftEyeSetup');
        break;
      case step.endsWith('Setup'):
        setCanProgress(true);
        selectedEye = step.split('Setup')[0];
        if (selectedEye === 'leftEye' && testData.testRightEye) {
          updateTestData({ testInProgress: true });
        } else updateTestData({ testInProgress: false });
        setNextStep(`${selectedEye}Test`);
        setBackStep(selectedEye === 'leftEye' && testData.testRightEye ? 'rightEyeSetup' : 'eyeSetup');
        break;
      case step.endsWith('Test'):
        setCanProgress(true);
        selectedEye = step.split('Test')[0];
        clearCurrentEyeResults(selectedEye);
        updateTestData({ testInProgress: true });
        setNextStep(selectedEye === 'rightEye' && testData.testLeftEye ? 'leftEyeSetup' : 'results');
        setBackStep(`${selectedEye}Setup`);
        break;
      default:
        break;
    }
  }, [step, testData.testRightEye, testData.testLeftEye, testData.testBothEyes, clearCurrentEyeResults, updateTestData]);

  const handleFinalResultsSubmission = useCallback(() => {
    const eyeTested = step.split('T')?.[0];
    const results = testData.contrastSensitivityResults || [];

    if (!results.some((result) => result[eyeTested])) {
      if (window.confirm('No results have been saved for this test. Are you sure you want to continue?')) {
        setStep('results');
        postMessage({ type: 'endOfTest' });
      } else setBackStep(`${eyeTested}Setup`);
    } else setStep('results');
  }, [postMessage, step, testData.contrastSensitivityResults]);

  const updateStep = useCallback(
    (selectedStep) => {
      setCanProgress(false);
      if (selectedStep === 'selectTest') {
        setTestStep('selectTest');
        postMessage({ type: 'holding' });
      } else {
        setBackStep(step);
        if (selectedStep.endsWith('Setup')) postMessage({ type: 'holding' });
        if (selectedStep === 'results') {
          setCanProgress(true);
          handleFinalResultsSubmission();
        }
        return selectedStep !== 'results' ? setStep(selectedStep) : null;
      }
    },
    [handleFinalResultsSubmission, postMessage, step, setTestStep],
  );

  useEffect(() => {
    if (receivedMessage?.type === 'beginTestAgain') updateStep('eyeSetup');
  }, [receivedMessage, updateStep]);

  const renderSwitch = () => {
    switch (step) {
      case 'eyeSetup':
        return <ResponsiveEyeSelection setCanProgress={setCanProgress} />;
      case 'bothEyesSetup':
      case 'rightEyeSetup':
      case 'leftEyeSetup':
        return <ResponsiveLogmarSelection eyeTested={step.split('Setup')[0]} setLogmar={setLogmar} noCharacters />;
      case 'rightEyeTest':
      case 'leftEyeTest':
      case 'bothEyesTest':
        return (
          <ResponsiveContrastSensitivityTestOptician
            goBack={() => updateStep(backStep)}
            goToResults={() => updateStep(nextStep)}
            eyeTested={step.split('Test')[0]}
          />
        );
      case 'results':
        return <ResponsiveContrastSensitivityResults setActiveTab={setActiveTab} updateStep={(step) => updateStep(step)} />;
      default:
        return;
    }
  };

  return (
    <Flex column alignItems='center' justifyContent={smallScreen ? 'flex-start' : 'center'}>
      {renderSwitch()}
      {step !== 'results' && !step.endsWith('Test') && (
        <NavButtons marginTop='0.5rem' updateStep={(direction) => updateStep(direction === 'back' ? backStep : nextStep)} canProgress={canProgress} />
      )}
    </Flex>
  );
};

export default ResponsiveContrastSensitivityContainer;
